/**
 * CSV export for the Orders dashboard ("Download Orders CSV").
 *
 * One builder serves both order types, which have different shapes:
 * - Online orders keep customer details on `order.user` and items on `order.order.items`.
 * - Local orders are written by the iPad POS (`Order.toDictionary()` in OrderCounter) with flat
 *   `customerName` / `customerPhone` / `customerAddress` fields and items on `order.dishes`.
 */

import moment from 'moment';
import { transformOrderId } from 'util/orderUtils';

export const ORDERS_CSV_HEADER =
  'Order Number,Date,Customer Name,Table Number,Status,Phone Number,Delivery Address,Total';

// Helper function to get order total
const getOrderTotal = (order) => {
  // Handle online orders (order.order.items)
  if (order?.order?.items) {
    const total = order.order.items.filter((item) => item.dish?.isDeletedDish !== true).reduce((count, curItem) => {
      return count + curItem.quantity * curItem.subTotal;
    }, 0);
    return total.toFixed(2);
  }
  // Handle local orders (order.dishes)
  if (order?.dishes) {
    const total = order.dishes.reduce((count, curItem) => {
      const itemPrice = curItem.subTotal !== undefined ? curItem.subTotal : curItem.price;
      return count + curItem.quantity * itemPrice;
    }, 0);
    return total.toFixed(2);
  }
  return '0.00';
};

// Helper function to format date
const formatDate = (order) => {
  if (order && order.createdAt) {
    if (order.createdAt && typeof order.createdAt === 'object' && '_seconds' in order.createdAt) {
      return moment(new Date(order.createdAt._seconds * 1000)).format('DD/MM/YYYY HH:mm:ss');
    }
    return moment(order.createdAt).format('DD/MM/YYYY HH:mm:ss');
  }
  return 'N/A';
};

// Helper function to get table number
const getTableNumber = (order) => {
  return (order.restaurant && order.restaurant.tableId) || order.tableNumber || order.tableNo || 'N/A';
};

// Helper function to get delivery/customer address (online + local orders)
export const getDeliveryAddress = (order) => {
  if (order?.user?.address) return order.user.address;
  if (order?.customerAddress) return order.customerAddress;
  if (order?.address) return order.address;
  return '';
};

// Helper function to get order status
const getStatus = (order) => {
  return order.status || 'N/A';
};

// Helper function to get customer phone number
const getPhoneNumber = (order) => {
  return (order.user && order.user.phoneNumber) || order.customerPhone || 'N/A';
};

// Helper function to get customer name (online + local orders)
const getCustomerName = (order) => {
  return (order.user && order.user.name) || order.customerName || '';
};

// Escape quotes for CSV safety (double any embedded double-quote)
const escapeCsv = (value) => String(value ?? '').replace(/"/g, '""');

const toCsvRow = (order) => {
  const orderNumber = transformOrderId(order.id);
  const date = formatDate(order);
  const customerName = getCustomerName(order);
  const tableNumber = getTableNumber(order);
  const status = getStatus(order);
  const phoneNumber = getPhoneNumber(order);
  const deliveryAddress = getDeliveryAddress(order);
  const total = getOrderTotal(order);
  return `"${escapeCsv(orderNumber)}","${escapeCsv(date)}","${escapeCsv(customerName)}","${escapeCsv(tableNumber)}","${escapeCsv(status)}","${escapeCsv(phoneNumber)}","${escapeCsv(deliveryAddress)}","${escapeCsv(total)}"`;
};

/** Build the CSV text (header + one row per order), without the BOM. */
export const buildOrdersCsv = (orders) => {
  return `${ORDERS_CSV_HEADER}\n${orders.map(toCsvRow).join('\n')}`;
};
